import React from 'react';
import {
  EngagementManagingSourceModel,
  MediaModel,
  UserRoleModel,
  UserStatusModel,
  ClassificationTypeModel,
  EngagementModel,
  EngagementStatusModel
} from '@utmost.co/enterprise-models';
import { useFeatureFlag } from '../components/common/featureFlag/useFeatureFlag';

type WithWorkdayFlag = DeepMaybe<{
  workdayUser?: boolean;
  managingSources?: EngagementManagingSourceModel[];
}>;

type WithPrimaryEng = DeepMaybe<{
  primaryEngagement: { workerClassification?: { type?: ClassificationTypeModel; name?: string } };
}>;

type WithCurrentAndDraftEnd = DeepMaybe<{
  currentEngagements: { workerClassification?: { type: ClassificationTypeModel } }[];
  draftEngagements: { workerClassification?: { type: ClassificationTypeModel } }[];
}>;

export function isWorkdayUser(user?: WithWorkdayFlag) {
  return user?.workdayUser || user?.managingSources?.includes('WORKDAY');
}

export function isUtmostUser(user?: WithWorkdayFlag) {
  return !isWorkdayUser(user);
}

export function workerType(user?: WithPrimaryEng) {
  if (user?.primaryEngagement?.workerClassification?.name) {
    return user.primaryEngagement.workerClassification.name;
  }
  return '';
}

export const isContractor = (user?: WithCurrentAndDraftEnd): boolean =>
  [...(user?.currentEngagements || []), ...(user?.draftEngagements || [])].some(
    (engagement) => engagement?.workerClassification?.type === 'NON_EMPLOYEE'
  );

const isPrimaryEngagementContractor = (user?: WithPrimaryEng): boolean =>
  user?.primaryEngagement?.workerClassification?.type === 'NON_EMPLOYEE';

export const enrichUserName = (
  user?: WithCurrentAndDraftEnd &
    DeepMaybe<{
      fullName?: string;
      email?: string;
    }>,
  noSuffix?: boolean
): string =>
  (user?.fullName?.length ? `${user.fullName}${isContractor(user) && !noSuffix ? ' (C)' : ''}` : user?.email) ?? '';

export const enrichUserNameFromPrimaryEngagement = (user: WithPrimaryEng & DeepMaybe<{ fullName?: string }>): string =>
  `${user?.fullName} ${isPrimaryEngagementContractor(user) ? ' (C)' : ''}`;

export const EnrichUserTitle: React.FC<
  React.PropsWithChildren<{
    user?: WithCurrentAndDraftEnd & WithWorkdayFlag & DeepMaybe<{ title?: { value?: string } }>;
  }>
> = ({ user }) => {
  const contingentTypesEnabled = useFeatureFlag('contingent-types');
  const platformUserTitleDesc = isUtmostUser(user) && isContractor(user) ? '(Contractor)' : '';

  return (
    <>{`${user?.title ? user.title.value : 'Worker'}${contingentTypesEnabled ? ` ${platformUserTitleDesc}` : ''}`}</>
  );
};

export const EnrichEngagementTitle: React.FC<
  React.PropsWithChildren<DeepMaybe<{ engagement: Pick<EngagementModel, 'fullTitle'> }>>
> = ({ engagement }) => <>{`${engagement?.fullTitle ? engagement?.fullTitle : 'Worker'}`}</>;

export interface AvatarModel {
  uid?: Maybe<string>;
  email?: Maybe<string>;
  profilePhoto?: DeepMaybe<Pick<MediaModel, 'uid' | 'signedUrl'>>;
  personUid?: Maybe<string>;
  status?: Maybe<UserStatusModel>;
  fullName?: Maybe<string>;
  personName?: DeepMaybe<{
    fullName?: string;
  }>;
}

export const buildMediaPicUri = (user?: DeepMaybe<AvatarModel>): string => {
  const mediaSrc = user?.profilePhoto?.signedUrl;
  if (mediaSrc) {
    return mediaSrc;
  }

  const mediaUid = user?.profilePhoto?.uid || user?.personUid;
  if (mediaUid) {
    return `/api/media/${mediaUid}/content?defaultAvatar=false`;
  }

  return '';
};

export const isEngagementInactive = (engagement?: DeepMaybe<{ status?: EngagementStatusModel }>) =>
  engagement?.status === 'PRE_HIRE' || engagement?.status === 'OFFBOARDING' || engagement?.status === 'OFFBOARDED';

export const INTERNAL_USER_ROLES: Exclude<UserRoleModel[], 'EXTERNAL_APPROVER'> = [
  'GWG_WORKER',
  'INTEGRATION_CLIENT',
  'MSP_ADMIN',
  'MSP_USER',
  'SUPPLIER',
  'SUPPORT_USER',
  'TENANT_ADMIN',
  'TENANT_USER'
];
